import { HereGeocodeItem, HereGeocodePosition } from "../../models/MapModels"
import Technician from "../../modules/technicians/models/technician"
import { isTechLive, techLivePoint } from "../../core/TechLive"
import { haversineMiles } from "../../core/Helper"
import JobMetro from "../../modules/crm/base/models/JobMetro"
import { JobMetroLayerTypes } from "../../core/Enums"

/** The middle of the continental US: where a map with nothing to show opens. */
export const US_CENTER: HereGeocodePosition = { lat: 39.8097343, lng: -98.5556199 }

// One free tile source for both themes; dark mode inverts it with CSS (.rinvox-dark-tiles in
// App.css), since CARTO's dark basemap needs a paid key.
export const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
export const DARK_TILES_CLASS = "rinvox-dark-tiles"

/** A position reported this recently still counts as where the technician is. */
export const RECENT_LOCATION_MS = 2 * 60 * 60 * 1000

/**
 * A position that is somewhere. 0,0 is what a phone sends without a fix -- a point in the
 * Atlantic that made technicians "5,300 mi away" -- so it is no position at all.
 */
export const isRealPosition = (p?: { lat: number, lng: number } | null): p is HereGeocodePosition =>
    !!p && p.lat != null && p.lng != null && !(p.lat === 0 && p.lng === 0)
    && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180

/** Technician faces grow as the map zooms in, as they always have. */
export const markerSizeForZoom = (zoom: number) => zoom > 13 ? 50 : zoom > 8 ? 40 : 30

export type TechPositionSource = "live" | "recent" | "wakeup" | "last"

export interface TechPosition {
    position: HereGeocodePosition
    source: TechPositionSource
    /** When the position was reported; none for the wake-up address. */
    at?: Date
}

/**
 * Where a technician is, for a map and for a distance -- one rule, so the pin and the miles
 * beside their name never disagree: live; else a position from the last two hours; else their
 * wake-up address; else an old position, as a last resort.
 */
export const resolveTechPosition = (tech: Technician): TechPosition | undefined => {
    const point = techLivePoint(tech.id)
    const pos = point?.position ?? tech.techLocation
    const atRaw = point?.at ?? tech.techLocationLastUpdate
    const at = atRaw ? new Date(atRaw) : undefined
    const age = at ? Date.now() - at.getTime() : Infinity

    if (isRealPosition(pos)) {
        if (isTechLive(tech.id, at)) return { position: pos, source: "live", at }
        if (age < RECENT_LOCATION_MS) return { position: pos, source: "recent", at }
    }
    if (isRealPosition(tech.techWakeupLocation)) return { position: tech.techWakeupLocation, source: "wakeup" }
    if (isRealPosition(pos)) return { position: pos, source: "last", at }
    return undefined
}

/** Straight-line miles between two real positions; undefined when either is missing. */
export const milesBetween = (a?: HereGeocodePosition | null, b?: HereGeocodePosition | null) =>
    isRealPosition(a) && isRealPosition(b) ? haversineMiles(a, b) : undefined

/** "0.8 mi", "4.9 mi", "42 mi". */
export const formatMiles = (miles: number) => `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`

/** "14 min", "1 h 20 min". */
export const formatDuration = (seconds: number) => {
    const min = Math.max(1, Math.round(seconds / 60))
    if (min < 60) return `${min} min`
    return `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ""}`
}

/** "25 min ago", "3 h ago", "12 days ago", "7 months ago". */
export const timeAgo = (ms: number) => {
    const min = Math.max(1, Math.round(ms / 60000))
    if (min < 60) return `${min} min ago`
    const h = Math.round(min / 60)
    if (h < 48) return `${h} h ago`
    const d = Math.round(h / 24)
    if (d < 60) return `${d} days ago`
    return `${Math.round(d / 30)} months ago`
}

/** Whether a point lies inside one of a metro's areas (its circles, rectangles and polygons). */
export const metroCovers = (metro: JobMetro, p?: HereGeocodePosition | null) => {
    if (!isRealPosition(p)) return false
    return (metro.layers ?? []).some(layer => {
        if (layer.type === JobMetroLayerTypes.Circle && isRealPosition(layer.position))
            return haversineMiles(layer.position, p) * 1609.344 <= (layer.circleRadius ?? 0)

        const pts = layer.positions ?? []
        if (layer.type === JobMetroLayerTypes.Rectangle && pts.length >= 2) {
            const lats = pts.map(q => q.lat), lngs = pts.map(q => q.lng)
            return p.lat >= Math.min(...lats) && p.lat <= Math.max(...lats)
                && p.lng >= Math.min(...lngs) && p.lng <= Math.max(...lngs)
        }

        if (layer.type === JobMetroLayerTypes.Polygon && pts.length >= 3) {
            // Ray casting: an odd number of edges crossed going east means inside.
            let inside = false
            for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
                const a = pts[i], b = pts[j]
                if ((a.lat > p.lat) !== (b.lat > p.lat)
                    && p.lng < (b.lng - a.lng) * (p.lat - a.lat) / (b.lat - a.lat) + a.lng) inside = !inside
            }
            return inside
        }
        return false
    })
}

// Countries whose middle is known, so opening the map costs no lookup.
export const COUNTRY_VIEWS: Record<string, { lat: number, lng: number, zoom: number }> = {
    us: { ...US_CENTER, zoom: 4 },
    au: { lat: -23.79991207781832, lng: 133.61022346000624, zoom: 5 },
    nz: { lat: -41.667682908454275, lng: 173.41890734897805, zoom: 6 },
    ca: { lat: 56.130366, lng: -106.346771, zoom: 4 }
}

/** How close to go on an address: the more of it is known, the closer. */
export const zoomForAddress = (item: HereGeocodeItem) => {
    if (item.zoom) return item.zoom
    let z = 5
    if (item.address?.state) z += 1
    if (item.address?.city) z += 2
    if (item.address?.district || item.address?.county) z += 2
    if (item.address?.street) z += 1
    if (item.address?.houseNumber) z += 2
    return z
}
